// src/components/pages/Foam/FoamParametersDashboard.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Grid,
  Card,
  CardContent,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  Alert,
  Box,
  Divider,
  Stack,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ClearIcon from "@mui/icons-material/Clear";
import { asNashvilleLocal, nowAsNashvilleLocal, nashvilleLocalToISO } from "../../../utils/nashvilleTime";

import ZonesChart from "./charts/ZonesChart";
import SVPVChart from "./charts/SVPVChart";
import PressureChart from "./charts/PressureChart";
import WaterFlowChart from "./charts/WaterFlowChart";
import WaterTemperatureChart from "./charts/WaterTemperatureChart";

import { ChartZoomToolbar } from "../../common/chartZoom";
import FoamPressureAlarms from "../../common/FoamPressureAlarms";

import {
  getFoamLatest,
  getFoamSeries,
  getResolvedZoneLabels,
  DEFAULT_LABELS,
  ZONES as TEMP_ZONES,
  Q_KEYS,
  ALL_KEYS,
  type ZoneKey,
  type QKey,
  type MetricKey,
  type FoamReading,
  type FoamSeriesPoint,
} from "../../../api/foam";

/* =========================
   Persistence (localStorage)
   ========================= */
const LS_KEY = "foamDash:v1";
const AUTO_REFRESH_MS = 60_000; // 60 seconds
// How far a selected PLC may fall behind the freshest one before the dashboard
// stops waiting on it. The worker polls every 60s, so this is many missed
// cycles — long enough to ride out a blip, short enough that a PLC which has
// gone quiet does not leave the charts empty.
const PLC_SILENT_MS = 30 * 60_000;
// Target number of points to request per window; the server buckets to fit.
const CHART_MAX_POINTS = 1500;
// High-pressure alarm bits, charted with the water-flow step chart.
const HP_ALARM_KEYS = ["hpWarn", "hpShutdown"];
const HP_ALARM_LABELS: Record<string, string> = {
  hpWarn: "High-pressure warning (V60.1)",
  hpShutdown: "High-pressure emergency stop (V60.2)",
};
const HP_ALARM_COLORS = ["#ed6c02", "#d32f2f"];
const hpAlarmLabel = (k: string) => HP_ALARM_LABELS[k] ?? k;

type PersistedState = {
  plc?: string;
  rangeHrs?: number;
  startISO?: string | null;
  endISO?: string | null;
  selectedKeys?: MetricKey[]; // can be []
};

function loadPersisted(): PersistedState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as PersistedState;
    return obj ?? null;
  } catch {
    return null;
  }
}

function savePersisted(s: PersistedState) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch {
    // ignore quota/SSR/SSR issues
  }
}

// Read once at module init so first render can use persisted values
const persistedOnce = typeof window !== "undefined" ? loadPersisted() : null;

/* =========================
   Helpers
   ========================= */
const num = (n: number | null | undefined, d = 1) =>
  n == null ? "—" : Number(n).toFixed(d);

const isCancelError = (e: any) =>
  e?.name === "CanceledError" ||
  e?.code === "ERR_CANCELED" ||
  e?.message === "canceled" ||
  e?.message === "AbortError";

/* =========================
   Component
   ========================= */
const FoamParametersDashboard: React.FC = () => {
  const [, setPlcs] = useState<string[]>([]);

  // PLC (use persisted if present)
  const [plc, setPlc] = useState<string>(persistedOnce?.plc ?? "");

  // quick range + date pickers (use persisted if present)
  const [rangeHrs, setRangeHrs] = useState<number>(
    typeof persistedOnce?.rangeHrs === "number" && persistedOnce.rangeHrs > 0
      ? persistedOnce.rangeHrs
      : 4,
  );
  const [start, setStart] = useState<Date | null>(
    persistedOnce?.startISO ? asNashvilleLocal(new Date(persistedOnce.startISO)) : null,
  );
  const [end, setEnd] = useState<Date | null>(
    persistedOnce?.endISO ? asNashvilleLocal(new Date(persistedOnce.endISO)) : null,
  );
  const anchoredEndRef = useRef<Date | null>(null);

  // X-axis view window in TRUE epoch ms (matches series ts_ms). Re-pinned on each
  // fetch to exactly the window we requested, so data always fills the axis.
  const [viewDomain, setViewDomain] = useState<[number, number] | null>(null);

  // Up to two click-placed marker times (epoch ms). Two markers -> a shaded band
  // the user can zoom into. Shared across all charts on the page.
  const [markers, setMarkers] = useState<number[]>([]);

  // selection (temps + Q) — restore even if empty array
  const initialSelected: MetricKey[] = Array.isArray(
    persistedOnce?.selectedKeys,
  )
    ? (persistedOnce!.selectedKeys!.filter((k) =>
        (ALL_KEYS as readonly string[]).includes(k),
      ) as MetricKey[])
    : ([...TEMP_ZONES, ...Q_KEYS] as MetricKey[]);
  const [selectedKeys, setSelectedKeys] =
    useState<MetricKey[]>(initialSelected);

  // labels (defaults -> backend overrides)
  const [labels, setLabels] = useState<Record<MetricKey, string>>({
    ...DEFAULT_LABELS,
  });

  // data & UI
  const [points, setPoints] = useState<FoamSeriesPoint[]>([]);
  const [bucketSeconds, setBucketSeconds] = useState<number>(0);
  // instantaneous latest reading for the Snapshot tiles (not bucket-averaged)
  const [latest, setLatest] = useState<FoamReading | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track in-flight request so we can cancel it
  const currentReqRef = useRef<AbortController | null>(null);

  // Auto-refresh every 60s; cancels prior request via refresh() logic
  useEffect(() => {
    if (!plc) return; // nothing to poll yet

    const id = setInterval(() => {
      // keep the window anchored to "now" when using quick-range mode
      if (!start && !end) anchoredEndRef.current = nowAsNashvilleLocal();
      refresh();
    }, AUTO_REFRESH_MS);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plc, start, end, rangeHrs]);

  // cancel any in-flight on unmount
  useEffect(() => {
    return () => {
      currentReqRef.current?.abort();
    };
  }, []);

  /* Save to localStorage whenever key state changes */
  useEffect(() => {
    const startISO =
      start && Number.isFinite(start.getTime()) ? nashvilleLocalToISO(start) : null;
    const endISO =
      end && Number.isFinite(end.getTime()) ? nashvilleLocalToISO(end) : null;

    savePersisted({
      plc,
      rangeHrs,
      startISO,
      endISO,
      selectedKeys,
    });
  }, [plc, rangeHrs, start, end, selectedKeys]);

  /* Load PLCs and labels on mount (ignore cancel errors) */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    getFoamLatest(signal)
      .then((rows) => {
        // Freshest first. `plc` is the PLC's port — the API keys machine
        // identity off the port rather than the IP, so a PLC that moves to a
        // new address stays one machine and its history stays continuous.
        // This ordering still matters if a second foam PLC is ever added.
        const byRecency = [...rows].sort((a, b) =>
          String(b.ts_utc ?? "").localeCompare(String(a.ts_utc ?? ""))
        );
        const uniq = Array.from(new Set(byRecency.map((r) => r.plc)));
        setPlcs(uniq);

        // Hold the persisted choice only while it is still reporting;
        // otherwise follow the PLC that is actually sending data. This also
        // clears any selection persisted back when identity was the full
        // address, which no longer matches anything.
        const newestFor = new Map(
          byRecency.map((r) => [r.plc, new Date(r.ts_utc).getTime()])
        );
        const freshest = uniq[0] ?? "";
        setPlc((curr) => {
          if (!curr || !uniq.includes(curr)) return freshest;
          const seenAt = newestFor.get(curr) ?? 0;
          const newest = newestFor.get(freshest) ?? 0;
          return newest - seenAt > PLC_SILENT_MS ? freshest : curr;
        });
      })
      .catch((e) => {
        if (!isCancelError(e)) setError(e?.message ?? "Failed to load latest");
      });

    getResolvedZoneLabels(signal)
      .then((ls) => setLabels(ls as Record<MetricKey, string>))
      .catch((e) => {
        if (!isCancelError(e)) {
          // keep defaults silently if canceled; otherwise you can log if you want
          // setError(e?.message ?? "Failed to load labels");
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computeWindow = () => {
    if (start && end)
      return [start.getTime(), end.getTime()] as [number, number];
    const now = (anchoredEndRef.current ?? nowAsNashvilleLocal()).getTime();
    return [now - rangeHrs * 3600_000, now] as [number, number];
  };

  const refresh = async (win?: [number, number]) => {
    if (!plc) return;

    // cancel any previous fetch
    currentReqRef.current?.abort();
    const controller = new AbortController();
    currentReqRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const [sMs, eMs] = win ?? computeWindow();
      // sMs/eMs are Nashville-as-local epochs; convert to the true UTC window we
      // fetch, and pin the X-axis to THAT (true epoch) so it matches series ts_ms.
      const startISO = nashvilleLocalToISO(new Date(sMs));
      const endISO = nashvilleLocalToISO(new Date(eMs));
      setViewDomain([new Date(startISO).getTime(), new Date(endISO).getTime()]);

      const res = await getFoamSeries({
        plc,
        startISO,
        endISO,
        maxPoints: CHART_MAX_POINTS,
        signal: controller.signal,
      });

      // ignore late results from a stale request
      if (currentReqRef.current !== controller) return;

      setPoints(res.items);
      setBucketSeconds(res.bucket_seconds);
    } catch (e: any) {
      if (isCancelError(e)) {
        // silent: user triggered a newer fetch
        return;
      }
      setError(e?.message ?? "Failed to load readings");
    } finally {
      if (currentReqRef.current === controller) {
        currentReqRef.current = null;
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!plc) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plc, rangeHrs, start, end]);

  // Live snapshot values (instantaneous latest reading, not bucket-averaged).
  useEffect(() => {
    if (!plc) return;
    let alive = true;
    const controller = new AbortController();
    const load = () =>
      getFoamLatest(controller.signal)
        .then((rows) => {
          if (alive) setLatest(rows.find((r) => r.plc === plc) ?? null);
        })
        .catch(() => {
          /* keep last known */
        });
    load();
    const id = setInterval(load, AUTO_REFRESH_MS);
    return () => {
      alive = false;
      controller.abort();
      clearInterval(id);
    };
  }, [plc]);

  const handleQuickRangeClick = (hrs: number) => {
    anchoredEndRef.current = nowAsNashvilleLocal();
    setRangeHrs(hrs);
    setStart(null);
    setEnd(null);
    setMarkers([]); // new window -> drop markers
  };

  const clearRange = () => {
    setStart(null);
    setEnd(null);
    setMarkers([]); // new window -> drop markers
    const now = nowAsNashvilleLocal();
    anchoredEndRef.current = now;
    // fetch the quick-range base window explicitly (state above not applied yet)
    refresh([now.getTime() - rangeHrs * 3600_000, now.getTime()]);
  };

  // unified chart rows. Series points already carry epoch-ms (ts_ms) and numeric
  // 0/1 booleans, so no Date.parse / boolean coercion is needed here.
  const chartData = useMemo(() => {
    return points.map((d) => {
      const row: any = {
        tMs: d.ts_ms,
        sv: d.sv ?? null,
        pv: d.pv ?? null,
        lp: d.low_pressure ?? null,
        hp1: d.high_pressure_1 ?? null,
        hp2: d.high_pressure_2 ?? null,
        waterTemp: d.high_pressure_2 ?? null,
        // null before collection started, so the chart shows a gap, not "OFF"
        hpWarn:
          d.hp_warning_active == null ? null : d.hp_warning_active > 0 ? 1 : 0,
        hpShutdown:
          d.hp_shutdown_active == null ? null : d.hp_shutdown_active > 0 ? 1 : 0,
      };

      // temps as numbers
      for (const z of TEMP_ZONES) {
        row[z] = (d as any)[z] ?? null;
      }
      // Q bits as numeric 0/1 for plotting (server returns MAX(::int) per bucket)
      for (const q of Q_KEYS) {
        const v = (d as any)[q];
        row[q] = v == null ? null : v > 0 ? 1 : 0;
      }
      return row;
    });
  }, [points]);

  // filter bad timestamps
  const chartDataSafe = useMemo(
    () =>
      chartData.filter(
        (r) => Number.isFinite(r.tMs) && r.tMs > 0 && r.tMs < 8.64e13,
      ),
    [chartData],
  );

  // Selection split for charts
  const selectedTempZones = useMemo(
    () =>
      selectedKeys.filter((k) =>
        (TEMP_ZONES as readonly string[]).includes(k),
      ) as ZoneKey[],
    [selectedKeys],
  );
  const selectedQKeys = useMemo(
    () =>
      selectedKeys.filter((k) =>
        (Q_KEYS as readonly string[]).includes(k),
      ) as QKey[],
    [selectedKeys],
  );

  // Stable label getter for menus & chart legend
  const labelFor = useCallback((k: MetricKey) => labels[k] ?? k, [labels]);

  // Stable click handler (React.memo'd charts): place up to two markers; a third
  // click starts a fresh selection.
  const handleChartClick = useCallback((t: number) => {
    setMarkers((prev) => (prev.length >= 2 ? [t] : [...prev, t]));
  }, []);

  // Zoom = set the Start/End range to the marked band. Markers are true-epoch ms
  // (from series ts_ms); convert to Nashville-as-local Dates for the pickers +
  // fetch pipeline. The [start,end] effect then refetches at finer resolution.
  const zoomToSelection = useCallback(() => {
    if (markers.length !== 2) return;
    const [a, b] = [...markers].sort((x, y) => x - y);
    if (b <= a) return;
    setStart(asNashvilleLocal(new Date(a)));
    setEnd(asNashvilleLocal(new Date(b)));
    setMarkers([]);
  }, [markers]);

  const clearMarkers = () => setMarkers([]);

  // Reset zoom = clear the custom range, back to the quick range anchored at now.
  const resetZoom = () => {
    setMarkers([]);
    setStart(null);
    setEnd(null);
    const now = nowAsNashvilleLocal();
    anchoredEndRef.current = now;
    refresh([now.getTime() - rangeHrs * 3600_000, now.getTime()]);
  };

  // X-axis window (true epoch, matches series ts_ms).
  const chartDomain = viewDomain;

  return (
    <Grid
      container
      spacing={2}
      padding={{ xs: 1, sm: 2 }}
      maxWidth="xl"
      margin="auto"
    >
      {/* Controls */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Start"
                    value={start}
                    onChange={(v) => {
                      setStart(v);
                      setMarkers([]); // new window -> drop markers
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: "small" },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                      label="End"
                      value={end}
                      onChange={(v) => {
                        setEnd(v);
                        setMarkers([]); // new window -> drop markers
                      }}
                      slotProps={{
                        textField: { fullWidth: true, size: "small" },
                      }}
                    />
                  </LocalizationProvider>
                  <Tooltip title="Clear range">
                    <IconButton onClick={clearRange} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} sm={12} md={6}>
                <ToggleButtonGroup
                  value={String(rangeHrs)}
                  exclusive
                  onChange={(_, v) => v && handleQuickRangeClick(Number(v))}
                  fullWidth
                >
                  {[1, 4, 12, 24, 72, 168, 720].map((h) => (
                    <ToggleButton key={h} value={String(h)}>
                      {h < 24 ? `${h}H` : `${h / 24}D`}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  If a date range is set, it overrides the quick range.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Select
                  multiple
                  value={selectedKeys}
                  onChange={(e: SelectChangeEvent<MetricKey[]>) =>
                    setSelectedKeys(e.target.value as MetricKey[])
                  }
                  input={<OutlinedInput label="Zones" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as MetricKey[]).map((z) => (
                        <Chip size="small" key={z} label={labelFor(z)} />
                      ))}
                    </Box>
                  )}
                  fullWidth
                >
                  {/* Temps */}
                  {TEMP_ZONES.map((z) => (
                    <MenuItem key={z} value={z}>
                      <Checkbox checked={selectedKeys.indexOf(z) > -1} />
                      <ListItemText primary={labelFor(z)} />
                    </MenuItem>
                  ))}
                  {/* Q status */}
                  <Divider />
                  {Q_KEYS.map((q) => (
                    <MenuItem key={q} value={q}>
                      <Checkbox checked={selectedKeys.indexOf(q) > -1} />
                      <ListItemText primary={labelFor(q)} />
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => refresh()}
                  disabled={loading || !plc}
                >
                  {loading ? "Loading…" : "REFRESH"}
                </Button>
                {bucketSeconds > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 2 }}
                  >
                    Showing ~{Math.round(bucketSeconds / 60)}-min averages · zoom
                    in for detail
                  </Typography>
                )}
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Snapshot */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="h6" gutterBottom>
              Snapshot
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 6 }}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }}
                />
              }
              sx={{ flexWrap: "wrap", rowGap: 2 }}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">
                  BUTANE CONTROL
                </Typography>
                <Typography variant="h5">
                  {latest?.butane_control ? "ON" : "OFF"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  SV
                </Typography>
                <Typography variant="h5">
                  {num(latest?.sv ?? null)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  PV
                </Typography>
                <Typography variant="h5">
                  {num(latest?.pv ?? null)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  HIGH P1
                </Typography>
                <Typography variant="h5">
                  {num(latest?.high_pressure_1 ?? null)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  WATER TEMPERATURE
                </Typography>
                <Typography variant="h5">
                  {num(latest?.high_pressure_2 ?? null)}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <FoamPressureAlarms reading={latest} />
          </CardContent>
        </Card>
      </Grid>

      {/* Zoom controls (shared by all charts) */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ py: 1.5 }}>
            <ChartZoomToolbar
              markers={markers}
              zoomed={!!(start && end)}
              onClearMarkers={clearMarkers}
              onResetZoom={resetZoom}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Charts */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Zone temperatures ({selectedTempZones.length} selected)
            </Typography>
            <ZonesChart
              data={chartDataSafe}
              selected={selectedTempZones}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
              zoneLabel={(k) => labelFor(k as MetricKey)}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pressures
            </Typography>
            <PressureChart
              data={chartDataSafe}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
              warnAt={latest?.hp_warning_setpoint}
              shutdownAt={latest?.hp_shutdown_setpoint}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              High-Pressure Alarms
            </Typography>
            <WaterFlowChart
              data={chartDataSafe}
              selected={HP_ALARM_KEYS}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
              labelFor={hpAlarmLabel}
              colors={HP_ALARM_COLORS}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              SV vs PV
            </Typography>
            <SVPVChart
              data={chartDataSafe}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Water Temperature
            </Typography>
            <WaterTemperatureChart
              data={chartDataSafe}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Water Flow Status Zones (Q) */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Water Flow Status Zones ({selectedQKeys.length} selected)
            </Typography>
            <WaterFlowChart
              data={chartDataSafe}
              selected={selectedQKeys}
              domain={chartDomain}
              markers={markers}
              onChartClick={handleChartClick}
              onZoom={zoomToSelection}
              labelFor={(k) => labelFor(k as MetricKey)}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default FoamParametersDashboard;
